// UndoToast — Undo 전용 토스트 컴포넌트 (U53).
//
// 특징:
//   - 5초(또는 duration) 카운트다운 progress bar (CSS animation)
//   - "되돌리기" 버튼 → undoLast() 호출
//   - X 버튼 → clear() 호출 (즉시 닫힘)
//   - useUndoStack.current가 null이면 렌더 안 함
//
// 위치: ui-undo-toast-container (화면 우하단, .ui-toast-stack 위)
// App.tsx의 UndoToastMount 컴포넌트에서 마운트.

import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import type { UndoStackValue } from '../hooks/useUndoStack';

// ── Props ─────────────────────────────────────────────────

export interface UndoToastProps {
  /** useUndoStack 반환값 전체를 props로 받아 제어 역전 방지 */
  stack: UndoStackValue;
}

// ── UndoToast 컴포넌트 ─────────────────────────────────────

/**
 * Undo 토스트 컴포넌트.
 * stack.current가 null이면 null 반환 (DOM 없음).
 * progress bar는 CSS animation으로 구현 — JS 타이머 의존 없음.
 */
export function UndoToast({ stack }: UndoToastProps) {
  const { current, undoLast, clear } = stack;

  // progress bar 애니메이션을 CSS custom property로 제어
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!current || !progressRef.current) return;

    const duration = current.duration ?? 5000;

    // CSS animation: width 100% → 0% in `duration`ms
    // 이전 animation 리셋 후 재시작
    const bar = progressRef.current;
    bar.style.animation = 'none';
    // reflow 강제 (animation 리셋)
    void bar.offsetWidth;
    bar.style.animationDuration = `${duration}ms`;
    bar.style.animation = `undo-toast-progress ${duration}ms linear forwards`;
  }, [current]);

  if (!current) return null;

  return (
    <div
      className="undo-toast"
      role="status"
      aria-live="polite"
      aria-label="실행 취소 알림"
    >
      {/* 본문 */}
      <div className="undo-toast__body">
        <p className="undo-toast__description">{current.description}</p>
      </div>

      {/* 액션 영역 */}
      <div className="undo-toast__actions">
        <button
          type="button"
          className="undo-toast__undo-btn"
          onClick={() => {
            undoLast();
          }}
          aria-label="되돌리기 (Cmd+Z)"
        >
          되돌리기
        </button>
        <button
          type="button"
          className="undo-toast__close"
          onClick={clear}
          aria-label="닫기"
        >
          <X size={14} />
        </button>
      </div>

      {/* 카운트다운 progress bar (하단) */}
      <div className="undo-toast__progress-track" aria-hidden="true">
        <div
          ref={progressRef}
          className="undo-toast__progress-bar"
        />
      </div>
    </div>
  );
}
